import React from "react";
import { Toaster, toast as sonnerToast } from "sonner";
import { cn } from "../lib/cn";

export type ToastVariant = "info" | "success" | "warning" | "danger";

export type ToastOptions = {
  title?: string;
  message: string;
  variant?: ToastVariant;
  /**
   * Auto-dismiss timeout in ms. Use 0 to disable auto-dismiss.
   */
  durationMs?: number;
  /**
   * Optional action button.
   */
  action?: {
    label: string;
    onClick: () => void;
  };
};

export type ToastContainerProps = {
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  className?: string;
};

/**
 * Imperative toast API (sonner-based) with brand defaults.
 *
 * Usage:
 *   import { toast } from "../ui/Toast";
 *   toast.success("Saved", { title: "Success" });
 */
export const toast = {
  show: (opts: ToastOptions) =>
    sonnerToast(primaryText(opts), mapOptions(opts)),
  info: (message: string, opts?: Omit<ToastOptions, "message" | "variant">) =>
    sonnerToast.info(
      primaryText({ ...opts, message }),
      mapOptions({ ...opts, message, variant: "info" }),
    ),
  success: (
    message: string,
    opts?: Omit<ToastOptions, "message" | "variant">,
  ) =>
    sonnerToast.success(
      primaryText({ ...opts, message }),
      mapOptions({ ...opts, message, variant: "success" }),
    ),
  warning: (
    message: string,
    opts?: Omit<ToastOptions, "message" | "variant">,
  ) =>
    sonnerToast.warning(
      primaryText({ ...opts, message }),
      mapOptions({ ...opts, message, variant: "warning" }),
    ),
  danger: (message: string, opts?: Omit<ToastOptions, "message" | "variant">) =>
    sonnerToast.error(
      primaryText({ ...opts, message }),
      mapOptions({ ...opts, message, variant: "danger" }),
    ),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
};

function primaryText(opts: ToastOptions): string {
  return opts.title?.trim() || opts.message || "";
}

function descriptionText(opts: ToastOptions): string | undefined {
  // If there's a title, use message as description; otherwise no description.
  if (opts.title?.trim()) return opts.message;
  return undefined;
}

function mapOptions(opts: ToastOptions) {
  const { variant = "info", durationMs, action } = opts;

  return {
    duration: durationMs ?? 4000,
    description: descriptionText(opts),
    action:
      action &&
      ({
        label: action.label,
        onClick: action.onClick,
      } as const),
    className: "sonner-toast",
    icon: undefined, // let Sonner handle icons or use defaults
    type:
      variant === "danger"
        ? "error"
        : variant === "warning"
          ? "warning"
          : variant === "success"
            ? "success"
            : "info",
    unstyled: false,
  };
}

/**
 * Render once near the app root.
 */
export function ToastContainer({
  position = "top-right",
  className,
}: ToastContainerProps) {
  return (
    <Toaster
      position={position}
      richColors={false}
      expand={false}
      closeButton
      className={cn(className)}
      toastOptions={{
        classNames: {
          /**
           * Keep Sonner's layout intact and style it like the rest of the app:
           * - container matches app surfaces/cards
           * - action/cancel match our `Button` variants (primary/secondary)
           * - close button is clear/strong but doesn't break stacking/layout
           */
          toast:
            "sonner-toast rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:color-mix(in_oklab,var(--kkp-neutral-900)_80%,white_20%)] text-white shadow-[0_20px_40px_rgba(0,0,0,0.25)] backdrop-blur-[8px]",
          title:
            "text-[color:rgba(255,255,255,0.92)] text-sm font-semibold leading-5",
          description: "text-[color:var(--kkp-text-muted)] text-xs leading-5",
          actionButton: [
            // Match `Button` primary (pill gradient)
            "inline-flex items-center justify-center gap-2",
            "select-none whitespace-nowrap",
            "font-bold",
            "rounded-full",
            "transition",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--kkp-magenta)]/60 focus-visible:ring-offset-0",
            "px-3 py-1.5 text-xs",
            "text-white",
            "border border-transparent",
            "bg-gradient-to-br from-[color:var(--kkp-purple)] to-[color:var(--kkp-magenta)]",
            "shadow-[0_10px_18px_rgba(0,0,0,0.22),_inset_0_1px_0_rgba(255,255,255,0.15)]",
            "hover:brightness-[1.05]",
            "active:translate-y-[1px]",
          ].join(" "),
          cancelButton: [
            // Match `Button` secondary (pill, subtle surface)
            "inline-flex items-center justify-center gap-2",
            "select-none whitespace-nowrap",
            "font-bold",
            "rounded-full",
            "transition",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--kkp-magenta)]/60 focus-visible:ring-offset-0",
            "px-3 py-1.5 text-xs",
            "text-white",
            "border border-[color:var(--kkp-border)]",
            "bg-[color:var(--kkp-surface)]",
            "hover:bg-[color:var(--kkp-surface-2)]",
            "active:translate-y-[1px]",
          ].join(" "),
          closeButton: [
            // Circular close button + white hover border (per request)
            "inline-flex items-center justify-center",
            "w-7 h-7",
            "rounded-full",
            "border border-[color:var(--kkp-border)]",
            "bg-[color:var(--kkp-surface-3)]",
            "text-white",
            "shadow-none",
            // Sonner may apply a transparent/white hover; force ours to win.
            "hover:bg-[color:var(--kkp-surface-3)] hover:text-white",
            "hover:border-white",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--kkp-magenta)]/60",
            // Ensure any SVG-based icon stays visible regardless of how Sonner renders it.
            "[&_*]:text-white [&_*]:stroke-white [&_*]:fill-none [&_*]:opacity-100",
            // Prevent any mask/filter tricks from washing out the icon.
            "[&_*]:filter-none",
          ].join(" "),
        },
      }}
    />
  );
}
